import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Logo from "@/components/Logo";
import {
  LayoutDashboard,
  Users,
  UserCog,
  BookOpen,
  Store,
  ShoppingCart,
  ShoppingBag,
  FileBarChart,
  Wallet,
  LogOut,
  Menu,
  X,
} from "lucide-react";

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [treasuryAlerts, setTreasuryAlerts] = useState(0);
  const [checklistPending, setChecklistPending] = useState(0);
  const [lockedAccounts, setLockedAccounts] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    let mounted = true;
    const refresh = async () => {
      try {
        const [{ data }, svc] = await Promise.all([api.get("/treasury/summary"), api.get("/service-invoices").catch(() => ({ data: [] }))]);
        if (!mounted) return;
        let count = svc.data.filter((i) => !i.paid_at).length;
        if (user.role === "admin") {
          // Admin actionable: physical lines with a client-uploaded invoice but not fully paid +
          // e-com periods not yet fully invoiced or invoiced but not paid (pending/partial/invoiced)
          count += (data.payout_rows || []).filter((r) => (r.client_invoices_count || 0) > 0 && r.status !== "settled").length;
          count += (data.invoice_rows || []).filter((r) => r.status !== "paid").length;
        } else {
          // Client actionable: physical lines not paid yet + e-com periods invoiced but not yet paid
          count += (data.payout_rows || []).filter((r) => r.status !== "settled").length;
          count += (data.invoice_rows || []).filter((r) => r.status === "invoiced" || r.status === "partial").length;
        }
        setTreasuryAlerts(count);
      } catch {
        /* silent */
      }
    };
    refresh();
    window.addEventListener("treasury:changed", refresh);
    if (user.role === "admin") {
      api.get("/checklist").then(({ data }) => mounted && setChecklistPending(data.pending || 0)).catch(() => {});
    }
    // Re-fetch when location changes (after CRUD on Treasury page)
    return () => { mounted = false; window.removeEventListener("treasury:changed", refresh); };
  }, [user, location.pathname]);

  useEffect(() => {
    if (user?.role !== "admin") return undefined;
    let mounted = true;
    const poll = () => api.get("/users/locks/count").then(({ data }) => mounted && setLockedAccounts(data.count || 0)).catch(() => {});
    poll();
    const id = setInterval(poll, 60000);
    window.addEventListener("accounts:changed", poll);
    return () => { mounted = false; clearInterval(id); window.removeEventListener("accounts:changed", poll); };
  }, [user, location.pathname]);

  // Close the mobile drawer on navigation
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Client accounts get their own wording ("Mes rapports"…) when a *_client key exists
  const nav = (key) => {
    if (user?.role !== "admin") {
      const v = t(`nav.${key}_client`);
      if (v && v !== `nav.${key}_client`) return v;
    }
    return t(`nav.${key}`);
  };

  const links = [
    { to: "/dashboard", icon: LayoutDashboard, label: t("nav.dashboard"), id: "nav-dashboard", badge: checklistPending },
    { to: "/reports", icon: FileBarChart, label: nav("reports"), id: "nav-reports" },
    { to: "/sales", icon: ShoppingCart, label: nav("sales"), id: "nav-sales" },
    { to: "/ecommerce", icon: ShoppingBag, label: nav("ecommerce"), id: "nav-ecommerce" },
    { to: "/treasury", icon: Wallet, label: nav("treasury"), id: "nav-treasury", badge: treasuryAlerts },
    { to: "/magazines", icon: BookOpen, label: nav("magazines"), id: "nav-magazines" },
    { to: "/stores", icon: Store, label: t("nav.stores"), id: "nav-stores", adminOnly: true },
    { to: "/clients", icon: Users, label: t("nav.clients"), id: "nav-clients", adminOnly: true },
    { to: "/accounts", icon: UserCog, label: t("nav.accounts"), id: "nav-accounts", adminOnly: true, badge: lockedAccounts },
  ];

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const totalBadges = checklistPending + treasuryAlerts + lockedAccounts;

  return (
    <div className="lg:flex min-h-screen bg-slate-50">
      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between gap-3 h-14 px-4 bg-white border-b border-slate-200">
        <button
          onClick={() => setMenuOpen(true)}
          aria-label={t("nav.menu")}
          data-testid="mobile-menu-open"
          className="relative -ml-2 p-2 text-slate-800 hover:bg-slate-100 rounded-sm transition-colors"
        >
          <Menu size={20} strokeWidth={1.75} />
          {totalBadges > 0 && (
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-600 rounded-full" data-testid="mobile-menu-badge" />
          )}
        </button>
        <Logo className="h-6" testid="mobile-logo" />
        <LanguageSwitcher />
      </header>

      {/* Backdrop */}
      {menuOpen && (
        <div
          onClick={() => setMenuOpen(false)}
          data-testid="mobile-menu-backdrop"
          className="lg:hidden fixed inset-0 z-40 bg-slate-900/50"
        />
      )}

      {/* Sidebar / mobile drawer */}
      <aside
        data-testid="app-sidebar"
        className={`fixed inset-y-0 left-0 z-50 w-[82vw] max-w-xs overflow-y-auto border-r border-slate-200 bg-white flex flex-col transition-transform duration-200 lg:static lg:z-auto lg:w-64 lg:max-w-none lg:shrink-0 lg:translate-x-0 ${
          menuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-start justify-between gap-2">
            <div>
              <Logo className="h-7" testid="sidebar-logo" />
              <div className="mt-2 label-tracked" data-testid="sidebar-account-name">
                {user?.role === "admin" ? t("nav.brand") : (user?.client_name || user?.name)}
              </div>
            </div>
            <div className="hidden lg:block"><LanguageSwitcher /></div>
            <button
              onClick={() => setMenuOpen(false)}
              aria-label={t("common.close")}
              data-testid="mobile-menu-close"
              className="lg:hidden -mt-1 -mr-1 p-2 text-slate-600 hover:bg-slate-100 rounded-sm transition-colors"
            >
              <X size={18} strokeWidth={1.75} />
            </button>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {links.map(({ to, icon: Icon, label, id, adminOnly, badge }) => {
            if (adminOnly && user?.role !== "admin") return null;
            return (
              <NavLink
                key={to}
                to={to}
                onClick={() => setMenuOpen(false)}
                data-testid={id}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-all ${
                    isActive
                      ? "bg-slate-900 text-white"
                      : "text-slate-700 hover:bg-slate-100"
                  }`
                }
              >
                <Icon size={16} strokeWidth={1.75} />
                <span className="flex-1">{label}</span>
                {badge > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-mono font-medium bg-red-600 text-white rounded-sm" data-testid={`${id}-badge`}>
                    {badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-200">
          <div className="px-3 py-2">
            <div className="label-tracked">{t("nav.connected")}</div>
            <div className="mt-1 text-sm font-medium text-slate-900 truncate" data-testid="user-name">
              {user?.name}
            </div>
            <div className="text-xs text-slate-600 truncate">{user?.email}</div>
            <div className="mt-1 inline-block text-[10px] font-mono tracking-wider uppercase bg-slate-100 border border-slate-200 px-1.5 py-0.5">
              {user?.role}
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="logout-button"
            className="w-full mt-2 flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-sm transition-all"
          >
            <LogOut size={16} strokeWidth={1.75} />
            <span>{t("nav.logout")}</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
